import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { ShoppingCart, Plus, Check } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  track: "Tracks",
  roller: "Rollers",
  handle: "Handles",
  lock: "Locks",
  spring: "Springs",
  mesh: "Mesh",
  frame: "Frames",
  hardware: "Hardware",
  other: "Other",
};

function ItemCard({ item, justAdded, onAdd }) {
  const out = !item.in_stock || (item.stock_count != null && item.stock_count <= 0);
  return (
    <div data-testid={`catalog-card-${item.slug}`}
      className="bg-white rounded-2xl border border-[var(--brand-border)] overflow-hidden flex flex-col">
      <div className="relative" style={{ background: "#f5f7f6", aspectRatio: "1 / 1" }}>
        {item.image_url ? (
          <img src={item.image_url} alt={item.name} loading="lazy"
            className="w-full h-full object-contain p-4" />
        ) : (
          <div className="w-full h-full flex items-center justify-center muted-text text-xs">No image</div>
        )}
        {out && (
          <span className="absolute top-2 right-2 text-[10px] font-extrabold uppercase tracking-wider bg-red-100 text-red-700 px-2 py-1 rounded-full">
            Out of stock
          </span>
        )}
        {!out && item.stock_count != null && item.stock_count <= 3 && (
          <span className="absolute top-2 right-2 text-[10px] font-extrabold uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-1 rounded-full">
            Only {item.stock_count} left
          </span>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <p className="text-[10px] font-bold uppercase tracking-wider muted-text">
          {CATEGORY_LABELS[item.category] || item.category}{item.sku ? ` · ${item.sku}` : ""}
        </p>
        <p className="font-bold text-[var(--brand-text)] mt-0.5">{item.name}</p>
        {item.description && (
          <p className="text-xs muted-text mt-1 leading-snug line-clamp-3">{item.description}</p>
        )}
        <div className="mt-auto pt-3 flex items-center justify-between">
          <span className="text-xl font-extrabold text-[var(--brand-text)]">${Number(item.price).toFixed(2)}</span>
          <button
            data-testid={`add-to-cart-${item.slug}`}
            onClick={() => onAdd(item)}
            disabled={out}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ${
              out
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : justAdded
                ? "bg-[var(--brand-green)] text-white"
                : "border-2 border-[var(--brand-border)] hover:border-[var(--brand-green)]"
            }`}
          >
            {out ? "Sold out" : justAdded ? <><Check className="w-3.5 h-3.5" /> Added</> : <><Plus className="w-3.5 h-3.5" /> Add</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PartsCatalogPage() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [justAdded, setJustAdded] = useState(null);
  const { add, itemCount } = useCart();

  useEffect(() => {
    setLoading(true);
    api.get("/catalog", { params: activeCategory ? { category: activeCategory } : {} })
      .then((r) => {
        setItems(r.data.items || []);
        if (categories.length === 0) setCategories(r.data.categories || []);
      })
      .catch(() => toast.error("Failed to load catalog"))
      .finally(() => setLoading(false));
  }, [activeCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = (item) => {
    add(item, 1);
    setJustAdded(item.slug);
    setTimeout(() => setJustAdded((s) => (s === item.slug ? null : s)), 1100);
  };

  // Only show category pills that actually have items
  const presentCategories = Array.from(new Set(items.map((i) => i.category)));
  const visibleCategories = activeCategory
    ? categories.filter((c) => c === activeCategory || presentCategories.includes(c))
    : categories.filter((c) => presentCategories.includes(c));

  return (
    <div className="min-h-screen bg-[var(--brand-bg)]">
      <div className="max-w-6xl mx-auto px-5 py-10">
        <p className="text-xs font-bold uppercase tracking-wider brand-text">Parts &amp; accessories</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--brand-text)] mt-1">
          Off-the-shelf parts, ready to ship.
        </h1>
        <p className="muted-text mt-3 max-w-xl">
          Tracks, rollers, locks, handles, and the everyday hardware we use in our own builds.
          Pick up at the shop or get them delivered locally for $39.
        </p>

        {visibleCategories.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-6">
            <button
              data-testid="catalog-cat-all"
              onClick={() => setActiveCategory("")}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 ${
                activeCategory === "" ? "brand-bg text-white border-transparent" : "border-[var(--brand-border)]"
              }`}
            >
              All
            </button>
            {visibleCategories.map((c) => (
              <button key={c} data-testid={`catalog-cat-${c}`}
                onClick={() => setActiveCategory(c)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 ${
                  activeCategory === c ? "brand-bg text-white border-transparent" : "border-[var(--brand-border)]"
                }`}>
                {CATEGORY_LABELS[c] || c}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="muted-text mt-10">Loading…</p>
        ) : items.length === 0 ? (
          <div className="mt-12 text-center py-16 bg-white rounded-3xl border border-[var(--brand-border)]">
            <p className="font-bold text-[var(--brand-text)]">Nothing here yet</p>
            <p className="text-sm muted-text mt-1">Looking for something specific? <a href="mailto:orders@screenzz.com" className="brand-text underline">Drop us a line</a>.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
            {items.map((it) => (
              <ItemCard key={it.slug} item={it} justAdded={justAdded === it.slug} onAdd={handleAdd} />
            ))}
          </div>
        )}
      </div>

      {itemCount > 0 && (
        <Link
          to="/parts/cart"
          data-testid="floating-cart-btn"
          className="fixed bottom-5 right-5 z-30 brand-bg text-white px-5 py-3 rounded-full shadow-xl flex items-center gap-2 font-semibold"
        >
          <ShoppingCart className="w-4 h-4" />
          Cart · {itemCount}
        </Link>
      )}
    </div>
  );
}
